import Accordion from '@base/Accordion/Accordion';
import styled from '@emotion/styled';
import { Fonts, mediaQuery, Spacings } from '@styles/index';
import React, { useState } from 'react';
import ChevronRight from '@icon/ChevronRight';
import Link from 'next/link';
import Head from 'next/head';
import { SEOScript } from '@seo/index';
import faqsSchema from '@seo/schemas/unacademyIcons/faqsSchema';

type FAQ = {
  key: string;
  value: string;
  link?: string;
};

interface FaqsProps {
  faqs: FAQ[];
}

const Wrapper = styled.div`
  display: flex;
  flex-wrap: wrap;
  flex-direction: row;
  margin: 0px -8px;
  ${mediaQuery.uptoTablet} {
    margin: 0px;
  }
  ${mediaQuery.uptoMobile} {
    width: 100%;
    margin: 0px;
    justify-content: center;
    flex-direction: column;
  }
`;

const StyledAccordion = styled(Accordion)<{ $isExpanded?: boolean }>`
  &.MuiAccordion-root {
    padding: ${Spacings.SPACING_10B} ${Spacings.SPACING_8B} !important;
    margin-top: 0px !important;
    margin-bottom: ${Spacings.SPACING_4B} !important;
    min-width: calc(50% - 16px);
    margin-left: ${Spacings.SPACING_2B} !important;
    margin-right: ${Spacings.SPACING_2B} !important;
    background: var(--color-i-icon-dark-primary);
    color: var(--color-i-icon-light-tertiary);
    box-shadow: none;
    height: ${({ $isExpanded }) => $isExpanded && '100%'};
    flex: 0 0 25%;
    max-width: 25%;
    ${mediaQuery.uptoMobile} {
      margin-left: ${Spacings.SPACING_4B} !important;
      margin-right: ${Spacings.SPACING_4B} !important;
      flex: 50%;
      max-width: 100%;
      padding: ${Spacings.SPACING_5B} ${Spacings.SPACING_4B} !important;
    }
  }
  .goal-card-container {
    padding: ${Spacings.SPACING_2B} ${Spacings.SPACING_0B};
    margin: ${Spacings.SPACING_1B} ${Spacings.SPACING_0B};
    box-shadow: none;
  }
`;

const StyledAccordionSummary = styled(Accordion.Summary)`
  &.MuiAccordionSummary-root {
    min-height: 0px;
  }
  .MuiAccordionSummary-content {
    display: flex !important;
    align-items: center !important;
  }
`;

const StyledQuestion = styled.h2`
  font-size: ${Fonts.H5};
  font-weight: ${Fonts.BOLD};
  ${mediaQuery.uptoMobile} {
    font-size: ${Fonts.H6};
    line-height: 1.5;
  }
`;

const StyleAccordionDetails = styled(Accordion.Details)`
  margin-top: ${Spacings.SPACING_4B};
  color: var(--color-i-icon-dark-secondary);
  font-size: ${Fonts.H6};
  line-height: ${Spacings.SPACING_5B};
  &::selection {
    color: var(--color-i-all-black);
    background: var(--color-i-icon-light-tertiary);
  }
`;

const StyledChevron = styled(ChevronRight)`
  transform: ${({ $expanded }) =>
    $expanded ? 'rotate(-90deg)' : 'rotate(-270deg)'};
  transition: transform 150ms ease;
  margin-left: auto;
  flex-shrink: 0;
`;

const StyledLink = styled.a`
  color: white;
  cursor: pointer;
  text-decoration: underline;
`;

const Faqs: React.FC<FaqsProps> = ({ faqs }) => {
  const [expandedIndex, setExpandedIndex] = useState(-1);
  const handleAccordionClick = (index) => {
    setExpandedIndex(expandedIndex === index ? -1 : index);
  };

  const getValueLink = (faq) => {
    return (
      <span>
        {faq.value}{' '}
        <Link href={faq.link} passHref legacyBehavior>
          <StyledLink>here</StyledLink>
        </Link>
        .
      </span>
    );
  };
  return (
    <Wrapper>
      <Head>
        {SEOScript({
          schema: faqsSchema(faqs)
        })}
      </Head>
      {faqs.map((faq, index) => (
        <StyledAccordion
          key={faq.key}
          expanded={expandedIndex === index}
          $isExpanded={expandedIndex !== -1}
          onClick={() => handleAccordionClick(index)}
        >
          <StyledAccordionSummary>
            <StyledQuestion>{faq.key}</StyledQuestion>
            <StyledChevron
              color="var(--color-i-icon-dark-secondary)"
              size="24px"
              $expanded={expandedIndex === index}
            />
          </StyledAccordionSummary>
          <StyleAccordionDetails>
            {faq.link ? getValueLink(faq) : faq.value}
          </StyleAccordionDetails>
        </StyledAccordion>
      ))}
    </Wrapper>
  );
};

Faqs.displayName = 'Faqs';

export default Faqs;
